import moment, { Moment } from 'moment-timezone'
import React from 'react'
import VirtualCardRevisionsFrequency from 'src/components/VirtualCardRevisionsFrequency/VirtualCardRevisionsFrequency'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import { translateNode } from 'src/localization/translateNode'
import { ReceiptAttachmentResponse } from 'src/models/receiptAttachment.models'
import {
  RecurrenceModel,
  RecurrenceResponse,
} from 'src/models/recurrence.models'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { VirtualCardStatus as BudgetStatus } from 'src/models/virtualCard.models'
import { toDollars } from 'src/utils/currency.utils'
import {
  eventChanges,
  showNewUsers,
  showRemovedUsers,
} from 'src/utils/revisions.utils'
import { css } from 'styled-components'

// will delete unused events once all phases of this project are complete b/c we're unsure which we are keeping/deleting atm
export enum AccountRevisionEvent {
  Expired = 'EXPIRED',
  Recurred = 'RECURRED',
  Restored = 'RESTORED',
  Sent = 'SENT',
  Updated = 'UPDATED',
  BalanceUpdated = 'BALANCE_UPDATED',
  Created = 'CREATED',
  UsersAdded = 'USERS_ADDED',
  UserAdded = 'USER_ADDED',
  UsersRemoved = 'USERS_REMOVED',
  ChangesMade = 'CHANGES_MADE',
}

export interface AccountRevisionListResponse {
  id: string
  event: AccountRevisionEvent
  timestamp: string
  operator?: UserListResponse
  before?: RevisionDiffResponse
  after?: RevisionDiffResponse
}

export interface FieldChangeGroup {
  label: React.ReactElement | string | undefined
  changes: FieldChange[]
}

export interface FieldChange {
  message?: React.ReactElement | string
  label?: React.ReactElement | string
  value?: React.ReactElement | string
}

export class AccountRevisionListModel {
  public id: string
  public event: AccountRevisionEvent | undefined
  public timestamp: string
  public operator: UserListModel | undefined
  public before?: RevisionDiffModel
  public after?: RevisionDiffModel

  public constructor(data: AccountRevisionListResponse) {
    this.id = data.id
    this.event =
      data.event === AccountRevisionEvent.Recurred ||
      data.event === AccountRevisionEvent.BalanceUpdated
        ? data.event
        : eventChanges(data.before, data.after)
    this.timestamp = data.timestamp
    this.operator =
      data.operator !== undefined ? new UserListModel(data.operator) : undefined
    this.before =
      data.before !== undefined ? new RevisionDiffModel(data.before) : undefined
    this.after =
      data.after !== undefined ? new RevisionDiffModel(data.after) : undefined
  }

  public get dateLabel(): React.ReactElement | string {
    if (
      moment().subtract(1, 'days').format('l') ===
      moment.tz(this.timestamp, moment.tz.guess()).format('l')
    ) {
      return translateNode('Yesterday')
    } else if (
      moment().format('l') ===
      moment.tz(this.timestamp, moment.tz.guess()).format('l')
    ) {
      return translateNode('Today')
    } else {
      return moment.tz(this.timestamp, moment.tz.guess()).format('l')
    }
  }

  // TODO: feels bad to explicitly use guess here just to get a timezone label
  public get timeLabel(): React.ReactElement {
    const mom = moment.tz(this.timestamp, moment.tz.guess())
    return translateNode('${time}, ${timezone}', {
      time: mom.format('LT'),
      timezone: mom.format('z'),
    })
  }

  public get dateTimeLabel(): React.ReactElement {
    return translateNode('${date} at ${timeLabel}', {
      date: this.dateLabel,
      timeLabel: this.timeLabel,
    })
  }

  public timezonedDateLabel(timezone: string): string {
    return moment.tz(this.timestamp, timezone).format('l')
  }

  public timezonedTimeLabel(timezone: string): React.ReactElement {
    const mom = moment.tz(this.timestamp, timezone)
    return translateNode('${time}, ${timezone}', {
      time: mom.format('LT'),
      timezone: mom.zoneAbbr(),
    })
  }

  public timezonedDateTimeLabel(timezone: string): React.ReactElement {
    return translateNode('${date} at ${timeLabel}', {
      date: this.timezonedDateLabel(timezone),
      timeLabel: this.timezonedTimeLabel(timezone),
    })
  }

  public eventSubject(userId: string): React.ReactElement | string {
    if (this.operator !== undefined && this.operator.id === userId) {
      return translateNode('You')
    } else if (this.operator !== undefined) {
      return this.operator.fullNameOrEmail
    } else if (
      this.event === AccountRevisionEvent.Sent &&
      this.after?.cardholder !== undefined
    ) {
      return this.after.cardholder.truncatedFullNameOrEmail
    } else if (this.event === AccountRevisionEvent.Recurred) {
      return translateNode('This recurring budget')
    } else if (this.event === AccountRevisionEvent.Expired) {
      return translateNode('This budget')
    } else {
      return translateNode('The system')
    }
  }

  public eventObject(userId: string): React.ReactElement | string {
    if (this.event === AccountRevisionEvent.Sent) {
      if (userId === this.after?.recipient?.id) {
        return this.operator !== undefined && this.operator.id === userId
          ? translateNode('yourself')
          : translateNode('you')
      } else if (this.after?.recipient !== undefined) {
        return this.after.recipient.truncatedFullNameOrEmail
      } else {
        return translateNode('Unknown')
      }
    } else {
      return translateNode('Unknown')
    }
  }

  public displayNameChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    if (before?.displayName !== undefined && after?.displayName === undefined) {
      return {
        message: pending
          ? translateNode('Card Name removed from pending modification')
          : translateNode('Card Name removed'),
      }
    } else if (after?.displayName !== undefined) {
      return {
        label: translateNode('Name: '),
        value: after?.displayName,
      }
    } else {
      return undefined
    }
  }

  public limitChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
    currency: ExtendCurrency,
  ): FieldChange | undefined {
    if (
      before?.issuingLimitCents !== undefined &&
      after?.issuingLimitCents === undefined
    ) {
      if (pending) {
        return {
          message: translateNode('Card Name removed from pending modification'),
        }
      } else if (
        before?.issuingLimitCents === undefined &&
        after?.issuingLimitCents !== undefined
      ) {
        return {
          label: translateNode('Budget Limit: '),
          value: translateNode('Unlimited'),
        }
      }
    } else if (after?.issuingLimitCents !== undefined) {
      return {
        label: translateNode('Budget Limit: '),
        value: (
          <Crncy
            amount={toDollars(after?.issuingLimitCents)}
            currency={currency}
          />
        ),
      }
    } else {
      return undefined
    }
  }

  public balanceCentsChanges(
    before: RevisionDiffModel | AccountPendingRevisionModel | undefined,
    after: RevisionDiffModel | AccountPendingRevisionModel | undefined,
    pending: boolean,
    currency: ExtendCurrency,
  ): FieldChange | undefined {
    if (
      before?.remainingAmountCents !== undefined &&
      after?.remainingAmountCents === undefined
    ) {
      if (pending) {
        return {
          message: translateNode(
            'Available Credit removed from pending modification',
          ),
        }
      } else {
        return {
          label: translateNode('Budget Limit: '),
          value: translateNode('Unlimited'),
        }
      }
    } else if (this.event === AccountRevisionEvent.BalanceUpdated) {
      if (
        before?.remainingAmountCents !== undefined &&
        after?.remainingAmountCents === undefined
      ) {
        return {
          label: translateNode('Remaining on Budget: '),
          value: translateNode('Unlimited'),
        }
      } else if (after?.remainingAmountCents !== undefined) {
        if (this.event === AccountRevisionEvent.BalanceUpdated) {
          return {
            label: translateNode('Remaining on Budget: '),
            value: (
              <Crncy
                amount={toDollars(after?.remainingAmountCents)}
                currency={currency}
              />
            ),
          }
        }
        return {
          label: translateNode('Budget Limit: '),
          value: (
            <Crncy
              amount={toDollars(after?.remainingAmountCents)}
              currency={currency}
            />
          ),
        }
      }
    } else if (
      this.event === AccountRevisionEvent.Sent ||
      (this.event === AccountRevisionEvent.ChangesMade &&
        after?.remainingAmountCents)
    ) {
      return {
        label: translateNode('Remaining on Budget: '),
        value: (
          <Crncy
            amount={toDollars(after?.remainingAmountCents || 0)}
            currency={currency}
          />
        ),
      }
    }
  }

  public validFromChanges(
    before: RevisionDiffModel | AccountPendingRevisionModel | undefined,
    after: RevisionDiffModel | AccountPendingRevisionModel | undefined,
    pending: boolean,
    timezone: string,
  ): FieldChange | undefined {
    if (
      this.event !== AccountRevisionEvent.Sent &&
      after?.recurrence !== undefined
    ) {
      return undefined
    }

    const afterValidFromTimezonedString =
      after?.validFromTimezonedString(timezone)
    if (before?.validFrom !== undefined && after?.validFrom === undefined) {
      return {
        message: pending
          ? translateNode('Start Date removed from pending modification')
          : translateNode('Start Date removed'),
      }
    } else if (afterValidFromTimezonedString !== undefined) {
      return {
        label: translateNode('Start Date: '),
        value: afterValidFromTimezonedString,
      }
    }
  }

  public activeUntilChanges(
    before: RevisionDiffModel | AccountPendingRevisionModel | undefined,
    after: RevisionDiffModel | AccountPendingRevisionModel | undefined,
    pending: boolean,
    timezone: string,
  ): FieldChange | undefined {
    if (after?.recurrence !== undefined) {
      return undefined
    }
    const afterValidToTimezonedString =
      after?.activeUntilTimezonedString(timezone)
    if (before?.until !== undefined && after?.until === undefined) {
      if (pending) {
        return {
          message: translateNode(
            'Active Until removed from pending modification',
          ),
        }
      } else if (before?.until !== undefined && !after?.until) {
        return {
          label: translateNode('Active Until: '),
          value: translateNode('Never Ends'),
        }
      }
    } else if (afterValidToTimezonedString !== undefined) {
      return {
        label: translateNode('Active Until: '),
        value: afterValidToTimezonedString,
      }
    } else {
      return undefined
    }
  }

  public recurrenceChanges(
    before: RevisionDiffModel | AccountPendingRevisionModel | undefined,
    after: RevisionDiffModel | AccountPendingRevisionModel | undefined,
    pending: boolean,
    timezone: string,
  ): FieldChange | undefined {
    if (pending && after?.recurs === false) {
      return {
        message: translateNode('Recurring removed'),
      }
    }
    if (before?.recurrence !== undefined && after?.recurrence === undefined) {
      return {
        message: pending
          ? translateNode('Recurring removed from pending modification')
          : translateNode('Recurring removed'),
      }
    } else if (after?.recurrence !== undefined) {
      return {
        label: translateNode('Recurring: '),
        value: (
          <VirtualCardRevisionsFrequency
            recurrence={after.recurrence}
            timezone={timezone}
            balance={toDollars(after.recurrence.balanceCents)}
            currency={after.currency}
          />
        ),
      }
    } else {
      return undefined
    }
  }

  public statusChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    if (before?.status !== undefined && after?.status === undefined) {
      return {
        message: pending
          ? translateNode('Status removed from pending modification')
          : translateNode('Status removed'),
      }
    } else if (after?.status !== undefined && after.status.length > 0) {
      return {
        label: translateNode('Status: '),
        value: after.psuedoStatusLabel,
      }
    }
  }

  public usersChanges = (
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
  ): FieldChange | undefined => {
    if (!before?.users || !after?.users) return
    if (before?.users?.length < after?.users?.length) {
      const newUsers = showNewUsers(before?.users, after?.users)
      return {
        label: (
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            {newUsers.map((user, i) => (
              <span key={i}>
                {translateNode('${fullNameOrEmail} was added to the budget', {
                  fullNameOrEmail: user?.fullNameOrEmail,
                })}
              </span>
            ))}
          </div>
        ),
      }
    } else if (after?.users?.length < before?.users?.length) {
      const removedUsers = showRemovedUsers(before?.users, after?.users)
      return {
        label: (
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            {removedUsers.map((user, i) => (
              <span
                key={i}
                css={css`
                  display: flex;
                `}
              >
                {translateNode(
                  '${fullNameOrEmail} was removed from the budget',
                  {
                    fullNameOrEmail: user?.fullNameOrEmail,
                  },
                )}
              </span>
            ))}
          </div>
        ),
      }
    } else if (after?.users?.length === before?.users?.length) {
      const newUsers = showNewUsers(before?.users, after?.users)
      const removedUsers = showRemovedUsers(before?.users, after?.users)
      return {
        label: (
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            {newUsers.map((user, i) => (
              <span key={i}>
                {translateNode('${fullNameOrEmail} was added to the budget', {
                  fullNameOrEmail: user?.fullNameOrEmail,
                })}
              </span>
            ))}
            {removedUsers.map((user, i) => (
              <span
                key={i}
                css={css`
                  display: flex;
                `}
              >
                {translateNode(
                  '${fullNameOrEmail} was removed from the budget',
                  {
                    fullNameOrEmail: user?.fullNameOrEmail,
                  },
                )}
              </span>
            ))}
          </div>
        ),
      }
    }
  }

  public notesChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
  ): FieldChange | undefined {
    if (before?.notes !== undefined && after?.notes === undefined) {
      return {
        message: translateNode('Notes removed'),
      }
    } else if (after?.notes !== undefined) {
      return {
        label: translateNode('Notes: '),
        value: after.notes,
      }
    }
  }

  public activeChanges(
    timezone: string,
    currency: ExtendCurrency,
  ): FieldChange[] {
    const acs = []

    const bef = this.before
    const aft = this.after

    const displayNameChanges = this.displayNameChanges(bef, aft, false)
    const limitChanges = this.limitChanges(bef, aft, false, currency)
    const balanceCentsChanges = this.balanceCentsChanges(
      bef,
      aft,
      false,
      currency,
    )
    const validFromChanges = this.validFromChanges(bef, aft, false, timezone)
    const activeUntilChanges = this.activeUntilChanges(
      bef,
      aft,
      false,
      timezone,
    )
    const recurrenceChanges = this.recurrenceChanges(bef, aft, false, timezone)
    const statusChanges = this.statusChanges(bef, aft, false)
    const usersChanges = this.usersChanges(bef, aft)
    const notesChanges = this.notesChanges(bef, aft)

    if (notesChanges !== undefined) {
      acs.push(notesChanges)
    }
    if (statusChanges !== undefined) {
      acs.push(statusChanges)
    }
    if (displayNameChanges !== undefined) {
      acs.push(displayNameChanges)
    }
    if (limitChanges !== undefined) {
      acs.push(limitChanges)
    }
    if (balanceCentsChanges !== undefined) {
      acs.push(balanceCentsChanges)
    }
    if (validFromChanges !== undefined) {
      acs.push(validFromChanges)
    }
    if (activeUntilChanges !== undefined) {
      acs.push(activeUntilChanges)
    }
    if (recurrenceChanges !== undefined) {
      acs.push(recurrenceChanges)
    }
    if (usersChanges !== undefined) {
      acs.push(usersChanges)
    }
    return acs
  }

  public pendingChanges(
    timezone: string,
    currency: ExtendCurrency,
  ): FieldChange[] {
    const pcs = []

    const bef = this.before?.pending
    const aft = this.after?.pending

    const balanceCentsChanges = this.balanceCentsChanges(
      bef,
      aft,
      true,
      currency,
    )
    const validFromChanges = this.validFromChanges(bef, aft, true, timezone)
    const activeUntilChanges = this.activeUntilChanges(bef, aft, true, timezone)
    const recurrenceChanges = this.recurrenceChanges(bef, aft, true, timezone)

    if (balanceCentsChanges !== undefined) {
      pcs.push(balanceCentsChanges)
    }
    if (validFromChanges !== undefined) {
      pcs.push(validFromChanges)
    }
    if (activeUntilChanges !== undefined) {
      pcs.push(activeUntilChanges)
    }
    if (recurrenceChanges !== undefined) {
      pcs.push(recurrenceChanges)
    }

    return pcs
  }

  public fieldChangeGroups(
    userId: string | undefined,
    timezone: string | undefined,
    currency: ExtendCurrency | undefined,
  ): FieldChangeGroup[] {
    if (!userId || !timezone || !currency) return []
    const subject = this.eventSubject(userId)
    const object = this.eventObject(userId)
    const activeChanges = this.activeChanges(timezone, currency).sort((a) =>
      a.message !== undefined ? 1 : -1,
    )

    switch (this.event) {
      case AccountRevisionEvent.BalanceUpdated: {
        return this.after?.remainingAmountCents !== undefined
          ? this.operator !== undefined
            ? [
                {
                  label: translateNode(
                    '${subject} created, updated or deactivated a virtual card on the budget',
                    {
                      subject: subject,
                    },
                  ),
                  changes: activeChanges,
                },
              ]
            : [
                {
                  label: translateNode('A virtual card from the budget reset'),
                  changes: [],
                },
              ]
          : []
      }
      case AccountRevisionEvent.ChangesMade: {
        return [
          {
            label: translateNode('${subject} made changes to the budget', {
              subject: subject,
            }),
            changes: activeChanges,
          },
        ]
      }
      case AccountRevisionEvent.Sent: {
        return [
          {
            label: translateNode('${subject} sent this card to ${object}', {
              subject: subject,
              object: object,
            }),
            changes: activeChanges,
          },
        ]
      }
      case AccountRevisionEvent.Expired: {
        return this.operator?.id === userId
          ? [
              {
                label: translateNode('${subject} made changes to the budget', {
                  subject: subject,
                }),
                changes: activeChanges,
              },
            ]
          : [
              {
                label: translateNode('${subject} expired', {
                  subject: subject,
                }),
                changes: [],
              },
            ]
      }
      case AccountRevisionEvent.Recurred: {
        return [
          {
            label: this.after?.awaitingBudget
              ? translateNode(
                  '${subject} failed to reset due to budget constraints',
                  {
                    subject: subject,
                  },
                )
              : this.after?.recurrence !== undefined
                ? translateNode('${subject} has been reset', {
                    subject: subject,
                  })
                : translateNode('${subject} reset', {
                    subject: subject,
                  }),
            changes: activeChanges,
          },
        ]
      }
      case AccountRevisionEvent.Restored: {
        return [
          {
            label: translateNode('${subject} restored this card', {
              subject: subject,
            }),
            changes: activeChanges,
          },
        ]
      }
      case AccountRevisionEvent.Updated: {
        return [
          {
            label: translateNode('${subject} made changes', {
              subject: subject,
            }),
            changes: activeChanges,
          },
        ]
      }
      case AccountRevisionEvent.Created: {
        if (this.after?.displayName === undefined) return []
        return [
          {
            label: translateNode(
              '${operator} created a budget, ${budgetName}',
              {
                operator: subject,
                budgetName: this.after.displayName,
              },
            ),
            changes: activeChanges,
          },
        ]
      }
      case AccountRevisionEvent.UsersAdded: {
        return [
          {
            label: translateNode('Users were added to the budget'),
            changes: activeChanges,
          },
        ]
      }
      case AccountRevisionEvent.UserAdded: {
        return [
          {
            label: activeChanges[0].label,
            changes: [],
          },
        ]
      }
      case AccountRevisionEvent.UsersRemoved: {
        return [
          {
            label: activeChanges[0].label,
            changes: [],
          },
        ]
      }
      default: {
        return []
      }
    }
  }
}

export interface AccountPendingRevisionResponse {
  limitCents?: number
  remainingAmountCents?: number
  validFrom?: string
  validTo?: string
  until?: string
  recurs?: boolean
  recurrence?: RecurrenceResponse
  receiptAttachments?: ReceiptAttachmentResponse[]
  currency?: ExtendCurrency
  id?: string
}

export class AccountPendingRevisionModel {
  public limitCents?: number
  public remainingAmountCents?: number
  public validFrom?: string
  public validTo?: string
  public until?: string
  public recurs?: boolean
  public recurrence?: RecurrenceModel
  public receiptAttachments?: ReceiptAttachmentResponse[]
  public currency?: ExtendCurrency
  public id?: string

  public constructor(data: AccountPendingRevisionResponse) {
    this.limitCents = data.limitCents
    this.remainingAmountCents = data.remainingAmountCents
    this.validFrom = data.validFrom
    this.validTo = data.validTo
    this.until = data.until
    this.recurs = data.recurs
    this.recurrence =
      data.recurrence !== undefined
        ? new RecurrenceModel(data.recurrence)
        : undefined
    this.receiptAttachments = data.receiptAttachments
    this.currency = data.currency
    this.id = data.id
  }
  public get balance(): number | undefined {
    return this.remainingAmountCents !== undefined
      ? toDollars(this.remainingAmountCents)
      : undefined
  }

  public balanceLabel(
    currency: ExtendCurrency,
  ): React.ReactElement | undefined {
    return this.balance !== undefined ? (
      <Crncy currency={currency} amount={this.balance} />
    ) : undefined
  }

  public validToTimezonedMoment(timezone: string): Moment | undefined {
    return this.validTo !== undefined
      ? moment.tz(this.validTo, timezone).subtract(1, 'millisecond')
      : undefined
  }

  public validToTimezonedString(timezone: string): string | undefined {
    const mom = this.validToTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }

  public activeUntilTimezonedMoment(timezone: string): Moment | undefined {
    return this.until !== undefined
      ? moment.tz(this.until, timezone).subtract(1, 'millisecond')
      : undefined
  }

  public activeUntilTimezonedString(timezone: string): string | undefined {
    const mom = this.activeUntilTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }

  public validFromTimezonedMoment(timezone: string): Moment | undefined {
    return this.validFrom !== undefined
      ? moment.tz(this.validFrom, timezone)
      : undefined
  }

  public validFromTimezonedString(timezone: string): string | undefined {
    const mom = this.validFromTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }
}

export interface RevisionDiffResponse {
  displayName?: string
  currency?: ExtendCurrency
  remainingAmountCents?: number
  spentCents?: number
  awaitingBudget?: boolean
  validFrom?: string
  validTo?: string
  until?: string
  recurs?: boolean
  recurrence?: RecurrenceResponse
  receiptAttachments?: string[]
  pending?: AccountPendingRevisionResponse
  pendingModification?: boolean
  cardholder?: UserListResponse
  recipient?: UserListResponse
  status?: BudgetStatus
  notes?: string
  users?: UserListResponse[]
  issuingLimitCents?: number
}

export class RevisionDiffModel {
  public displayName?: string
  public currency?: ExtendCurrency
  public remainingAmountCents?: number
  public spentCents?: number
  public awaitingBudget?: boolean
  public validFrom?: string
  public validTo?: string
  public until?: string
  public recurs?: boolean
  public recurrence?: RecurrenceModel
  public receiptAttachments?: string[]
  public pending?: AccountPendingRevisionModel
  public pendingModification?: boolean
  public cardholder?: UserListModel
  public recipient?: UserListModel
  public status?: BudgetStatus
  public notes?: string
  public users?: UserListModel[]
  public issuingLimitCents?: number

  public constructor(data: RevisionDiffResponse) {
    this.displayName = data.displayName
    this.currency = data.currency
    this.remainingAmountCents = data.remainingAmountCents
    this.spentCents = data.spentCents
    this.awaitingBudget = data.awaitingBudget
    this.validFrom = data.validFrom
    this.validTo = data.validTo
    this.until = data.until
    this.recurs = data.recurs
    this.recurrence =
      data.recurrence !== undefined
        ? new RecurrenceModel(data.recurrence)
        : undefined
    this.receiptAttachments = data.receiptAttachments
    this.pending =
      data.pending !== undefined
        ? new AccountPendingRevisionModel(data.pending)
        : undefined
    this.pendingModification = data.pendingModification
    this.cardholder =
      data.cardholder !== undefined
        ? new UserListModel(data.cardholder)
        : undefined
    this.recipient =
      data.recipient !== undefined
        ? new UserListModel(data.recipient)
        : undefined
    this.status = data.status
    this.notes = data.notes
    this.users =
      data.users !== undefined
        ? data.users.map((user) => new UserListModel(user))
        : undefined
    this.issuingLimitCents = data.issuingLimitCents
  }

  public get isActive(): boolean {
    return this.status === BudgetStatus.Active
  }

  public get isPending(): boolean {
    return this.status === BudgetStatus.Pending
  }

  public get isInactive(): boolean {
    return !this.isActive && !this.isPending
  }

  public get isConsumed(): boolean {
    // subset of inactive
    return this.status === BudgetStatus.Consumed
  }

  public get psuedoStatusLabel(): React.ReactElement {
    return this.isPending
      ? translateNode('Pending')
      : this.isActive
        ? translateNode('Active')
        : this.isConsumed
          ? translateNode('Closed')
          : translateNode('Inactive')
  }

  public get balance(): number | undefined {
    return this.remainingAmountCents !== undefined
      ? toDollars(this.remainingAmountCents)
      : undefined
  }

  public validToTimezonedMoment(timezone: string): Moment | undefined {
    return this.validTo !== undefined
      ? moment.tz(this.validTo, timezone).subtract(1, 'millisecond')
      : undefined
  }

  public validToTimezonedString(timezone: string): string | undefined {
    const mom = this.validToTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }

  public activeUntilTimezonedMoment(timezone: string): Moment | undefined {
    return this.until !== undefined
      ? moment.tz(this.until, timezone).subtract(1, 'millisecond')
      : undefined
  }

  public activeUntilTimezonedString(timezone: string): string | undefined {
    const mom = this.activeUntilTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }

  public validFromTimezonedMoment(timezone: string): Moment | undefined {
    return this.validFrom !== undefined
      ? moment.tz(this.validFrom, timezone)
      : undefined
  }

  public validFromTimezonedString(timezone: string): string | undefined {
    const mom = this.validFromTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }
}
